'use client';

import { checkIsAdminRole, useSession } from '@repo/auth';
import { navigateToChromeHref } from '@repo/web-chrome/chrome-config/navigate-to-chrome-href';
import { useOptionalChromeConfig } from '@repo/web-chrome/chrome-config/use-optional-chrome-config';
import { useLocalePathname, useLocaleRouter } from '@repo/web-chrome/lib/i18n/navigation';
import { isEditableTarget } from '@repo/web-chrome/lib/is-editable-target';
import { isOnSysRoute } from '@repo/web-chrome/lib/is-on-sys-route';
import { useEffect } from 'react';

interface IShortcutState {
  isLoggedIn: boolean;
  isAdmin: boolean;
}

function hasModifier(e: KeyboardEvent): boolean {
  return e.metaKey || e.ctrlKey || e.altKey || e.shiftKey;
}

function resolveShortcutPath(key: string, { isLoggedIn, isAdmin }: IShortcutState): string | null {
  if (key === 'l' && !isLoggedIn) {
    return '/login';
  }
  if (key === 'a' && isLoggedIn) {
    return '/my';
  }
  if (key === 's' && isAdmin) {
    return '/sys';
  }
  return null;
}

export function useNavShortcuts(): void {
  const router = useLocaleRouter();
  const chromeConfig = useOptionalChromeConfig();
  const pathname = useLocalePathname();
  const { data: sessionData, isPending } = useSession();
  const isLoggedIn = Boolean(sessionData?.session);
  const isAdmin = checkIsAdminRole({ role: sessionData?.user?.role });

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (isPending || hasModifier(e) || isEditableTarget(e.target) || isOnSysRoute(pathname)) {
        return;
      }
      const path = resolveShortcutPath(e.key.toLowerCase(), { isLoggedIn, isAdmin });
      if (path) {
        e.preventDefault();
        navigateToChromeHref(chromeConfig, router, path);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isLoggedIn, isAdmin, isPending, pathname, router, chromeConfig]);
}
